import type { DSLFile, FormComponent, FormDSL, FormModule, FormStep } from '@commenda/types';

/**
 * Generic DSL component walker. Visits every component in the DSL exactly once
 * (step → module → component, top-level only). Does NOT recurse into
 * component.template; consumers that need that pattern should iterate
 * `component.template` from within the visitor.
 *
 * Null/undefined-safe at every nesting level so it can be called on partially-
 * formed DSLs (e.g. mid-edit drafts) without throwing.
 */
export function walkComponents(
  dsl: DSLFile | FormDSL,
  visit: (
    component: FormComponent,
    ctx: { step: FormStep; module: FormModule }
  ) => void
): void {
  const steps = dsl?.steps ?? [];
  steps.forEach((step) => {
    const modules = step?.modules ?? [];
    modules.forEach((module) => {
      const components = module?.components ?? [];
      components.forEach((component) => {
        visit(component, { step, module });
      });
    });
  });
}

/**
 * Flatten the DSL to an array of every top-level component (in walk order).
 */
export function flattenComponents(dsl: DSLFile | FormDSL): FormComponent[] {
  const out: FormComponent[] = [];
  walkComponents(dsl, (component) => out.push(component));
  return out;
}

/**
 * List every component's fieldId + label. Returns only components that have a
 * fieldId.
 */
export function getAllFieldIds(
  dsl: FormDSL
): { fieldId: string; label: string }[] {
  const fields: { fieldId: string; label: string }[] = [];
  walkComponents(dsl, (component) => {
    if (component.fieldId) {
      fields.push({
        fieldId: component.fieldId,
        label: component.label || component.fieldId,
      });
    }
  });
  return fields;
}

/**
 * Extract fields from a form template DSL. Same output shape as
 * `getAllFieldIds`, but defensively guards against non-array `dsl.steps`
 * (returns an empty list rather than throwing) so it's safe on partially-
 * formed templates passed through loose casts.
 */
export function getFieldsFromTemplateDSL(
  dsl: FormDSL
): { fieldId: string; label: string }[] {
  const fields: { fieldId: string; label: string }[] = [];

  // Helper to extract from components array
  const extractFromComponents = (components: unknown[]) => {
    components.forEach((comp) => {
      const component = comp as Record<string, unknown>;
      if (component.fieldId && typeof component.fieldId === 'string') {
        fields.push({
          fieldId: component.fieldId,
          label: (component.label as string) || component.fieldId,
        });
      }
    });
  };

  // Helper to extract from modules array
  const extractFromModules = (modules: unknown[]) => {
    modules.forEach((mod: FormModule) => {
      if (Array.isArray(mod.components)) {
        extractFromComponents(mod.components);
      }
    });
  };

  // Helper to extract from steps array
  const extractFromSteps = (steps: unknown[]) => {
    steps.forEach((step: FormStep) => {
      if (Array.isArray(step.modules)) {
        extractFromModules(step.modules);
      }
    });
  };

  // Check for full form template structure (has steps)
  if (Array.isArray(dsl.steps)) {
    extractFromSteps(dsl.steps);
  }

  return fields;
}

/**
 * Build a map: fieldId -> "StepName.ModuleName.FieldLabel".
 * Used by the renderer to surface human-readable labels in validation errors.
 */
export function buildFieldLabelMap(dsl: DSLFile): Record<string, string> {
  const map: Record<string, string> = {};
  walkComponents(dsl, (component, { step, module }) => {
    if (!component.fieldId) return;

    const stepName = (step.title || step.id || '').trim();
    const moduleName = (module.name || module.id || '').trim();
    const baseLabel = (component.label || component.fieldId || '')
      .toString()
      .trim();

    const parts: string[] = [];
    if (stepName) parts.push(stepName);
    if (moduleName) parts.push(moduleName);
    if (baseLabel) parts.push(baseLabel);

    map[component.fieldId] = parts.join('.');
  });
  return map;
}

/**
 * Keys a repeatable module stores its instance array under. These hold form
 * data, not a single entity's fields, so consumers pushing an entry to
 * Commenda OS must leave them out.
 */
export function getRepeatableSaveKeys(dsl: DSLFile | FormDSL): string[] {
  const keys: string[] = [];
  (dsl?.steps ?? []).forEach((step) => {
    (step?.modules ?? []).forEach((module) => {
      if (!module?.repeatable?.enabled) return;
      const key = module.saveKey || module.id;
      if (key) keys.push(key);
    });
  });
  return keys;
}

/**
 * Build a map: fieldId -> FormComponent. Used by the renderer to resolve a
 * field's component definition by id (e.g. when evaluating bindings).
 */
export function buildComponentMap(dsl: DSLFile): Record<string, FormComponent> {
  const map: Record<string, FormComponent> = {};
  walkComponents(dsl, (component) => {
    if (component.fieldId) {
      map[component.fieldId] = component;
    }
  });
  return map;
}
